import {
  Component,
  ChangeDetectionStrategy,
  OnDestroy,
  inject,
  output,
  input,
  model,
  signal,
  viewChild,
} from '@angular/core';
import {
  ComboboxComponent,
  DropdownsItemTemplateDirective,
  MultiselectComponent,
  NoDataTemplateDirective,
  TagTemplateDirective,
} from '@cue/assist/ui/dropdowns';
import { TranslocoPipe } from '@jsverse/transloco';

import { map } from 'rxjs/operators';
import { forkJoin, Observable, of, Subscription } from 'rxjs';
import { translation } from '@cue/translations';
import {
  AttendeeDropdownItem,
  AttendeesDropdownItemComponent,
} from '../attendees-dropdown-item/attendees-dropdown-item.component';

import { ConfigService, DataService, isValidEmail } from '@assist/shared/data';
import { AttendeeFilterItem } from '@assist/ui/attendee-filter';
import { CalendarsService } from '@cue/calendars';
import { NgStyle } from '@angular/common';
import { Validators } from '@angular/forms';

@Component({
  selector: 'assist-attendees-dropdown',
  templateUrl: './attendees-dropdown.component.html',
  styleUrls: ['./attendees-dropdown.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    TranslocoPipe,
    AttendeesDropdownItemComponent,
    ComboboxComponent,
    MultiselectComponent,
    DropdownsItemTemplateDirective,
    NoDataTemplateDirective,
    TagTemplateDirective,
    NgStyle,
  ],
})
export class AttendeesDropdownComponent implements OnDestroy {
  private dataService = inject(DataService);
  private configService = inject(ConfigService);
  private calendarsService = inject(CalendarsService);
  private combobox = viewChild(ComboboxComponent);
  private multiselect = viewChild(MultiselectComponent);
  value = model<AttendeeDropdownItem[]>([]);
  filterValueChange = output<string>();
  single = input(false);
  from = input<Date>();
  to = input<Date>();
  lowerBound = input<Date>();
  preferEmail = input(false);
  upperBound = input<Date>();

  ignoreTimes = input(false);
  // Martine taky si uprav jak potrebujes
  subs: Subscription[] = [];
  translation = translation;
  loading = signal(false);
  data = signal<AttendeeDropdownItem[]>([]);

  onValueChange(value: any) {
    if (!value) return;

    if (this.single()) {
      this.value.set(value);
      return;
    }
    const groups = value.filter((x) => x.isGroup);

    if (groups.length > 0) {
      const obs = groups.map((g) => this.calendarsService.getContactsFromGroup(g.value));
      forkJoin(obs).subscribe((contacts: any) => {
        const valueWithoutGroups = value.filter((x) => !x.isGroup);
        const contactsAl: AttendeeFilterItem[] = contacts
          .reduce((acc, it) => [...acc, ...it], [])
          .map((contact) => ({
            value: contact.mail,
            text: contact.displayName,
            isGroup: false,
            remotely: false,
            optional: false,
          }));

        if (contactsAl.some((c) => c.value == null)) {
          console.warn("Some contacts don't have email address! Check your contatcbook.");
          console.warn(contactsAl.filter((x) => x.value == null));
        }

        this.value.set([...valueWithoutGroups, ...contactsAl.filter((x) => x.value != null)]);
      });
    } else {
      this.value.set(value);
    }
  }

  valueNormalizer(text$: Observable<string>) {
    return text$.pipe(
      map((text: string) => {
        const matchingValue: any = this.data().find((item: any) => {
          return item['value'].toLowerCase() === text.toLowerCase();
        });

        if (this.value().find((x) => x['value'].toLocaleLowerCase() === text.toLowerCase())) {
          return null;
        }
        if (matchingValue) {
          return matchingValue;
        } else {
          if (isValidEmail(text)) {
            return {
              value: text, //generate unique value for the custom item
              text: text,
            };
          } else {
            return null;
          }
        }
      })
    );
  }

  normalizer = this.valueNormalizer.bind(this);

  open() {
    if (this.single()) {
      this.combobox()?.open();
      return;
    }

    this.multiselect()?.openPopup();
  }

  onFilterChange(filterValue) {
    this.filterValueChange.emit(filterValue);

    const contact = this.calendarsService.findContact(filterValue).pipe(
      map((contacts) => ({
        contacts,
        getIdentifierFromContact: this.calendarsService.getIdentifierFromContact,
      })),

      map(({ contacts, getIdentifierFromContact }) =>
        contacts.map(
          (contact) =>
            ({
              value: this.preferEmail() ? contact.mail : getIdentifierFromContact(contact),
              text: contact.displayName,
              isGroup: false,
            }) as AttendeeDropdownItem
        )
      )
    );

    const searchUsers =
      this.configService.value.searchInUsers && filterValue !== ''
        ? this.dataService.searchAdminUsers(filterValue).pipe(
            map((users) =>
              users.map(
                (u) =>
                  ({
                    value: u.email,
                    text: u.firstName + ' ' + u.lastName,
                    isGroup: false,
                    remotely: false,
                    optional: false,
                  }) as AttendeeFilterItem
              )
            )
          )
        : of([]);

    const group = !this.single()!
      ? this.configService.value.enableGroupFinding
        ? this.calendarsService.findContactGroup(filterValue).pipe(
            map((contacts) =>
              contacts.map(
                (group) =>
                  ({
                    value: group.id,
                    text: group.displayName,
                    isGroup: true,
                  }) as AttendeeDropdownItem
              )
            )
          )
        : of([])
      : of([]);

    this.subs.push(
      forkJoin([contact, group, searchUsers]).subscribe(([contacts, groups, searchedUsers]) => {
        this.loading.set(false);
        const mergedUseds = [...contacts, ...searchedUsers];
        const uniqueContacts = mergedUseds.filter(
          (contact, index, self) => index === self.findIndex((c) => c.value === contact.value)
        );
        this.data.set([...groups, ...uniqueContacts]);
      })
    );
  }

  ngOnDestroy() {
    this.subs.forEach((s) => s.unsubscribe());
    // keep this
  }
}
